import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { EntityProfileIndustry } from "@common/types/entity";
import { FiltersTypes } from "@common/types/filters/general";
import type { ApplyCoaTemplateEntitySelectionRow } from "./applyCoaTemplateEntitySelection.types";

interface FilterByServicesAndTeamParams {
    rows: ApplyCoaTemplateEntitySelectionRow[];
    selectedServices: NonNullable<FiltersTypes["services"]>;
    selectedTeamMemberIds: NonNullable<FiltersTypes["teamMemberIds"]>;
}

/**
 * Drops whole workspace rows that don't match the client-level Services / Team
 * selections. Both are client-level, so a non-matching client removes its entire
 * workspace + entities. Returns the input unchanged when neither is active.
 * Shared by the apply Select-entities step and the applied-entities drawer.
 */
export function filterWorkspaceRowsByServicesAndTeam({
    rows,
    selectedServices,
    selectedTeamMemberIds,
}: FilterByServicesAndTeamParams): ApplyCoaTemplateEntitySelectionRow[] {
    const hasServiceFilter = selectedServices.length > 0;
    const hasTeamFilter = selectedTeamMemberIds.length > 0;
    if (!hasServiceFilter && !hasTeamFilter) {
        return rows;
    }
    return rows.filter((workspaceRow) => {
        if (
            hasServiceFilter &&
            !(workspaceRow.services ?? []).some((service) =>
                selectedServices.includes(service),
            )
        ) {
            return false;
        }
        if (
            hasTeamFilter &&
            !workspaceRow.team.some((member) =>
                selectedTeamMemberIds.includes(member.userId),
            )
        ) {
            return false;
        }
        return true;
    });
}

interface FilterSelectEntitiesRowsParams {
    rows: ApplyCoaTemplateEntitySelectionRow[];
    search: string | undefined;
    selectedIndustries: EntityProfileIndustry[];
    selectedBases: AccountingLedgerBasis[];
}

/**
 * Filters the workspace → entity tree by name search plus the entity-level
 * Industry / Basis selections. Search matches either the workspace name (keeping
 * all of its still-matching children) or the child name. Industry and Basis are
 * entity-level, so they filter children directly. Workspaces with no remaining
 * children are dropped. Returns the input unchanged when nothing is active.
 */
export function filterSelectEntitiesRows({
    rows,
    search,
    selectedIndustries,
    selectedBases,
}: FilterSelectEntitiesRowsParams): ApplyCoaTemplateEntitySelectionRow[] {
    const trimmed = search?.trim().toLowerCase();
    const hasSearch = !!trimmed;
    const hasIndustryFilter = selectedIndustries.length > 0;
    const hasBasisFilter = selectedBases.length > 0;

    if (!hasSearch && !hasIndustryFilter && !hasBasisFilter) {
        return rows;
    }

    const result: ApplyCoaTemplateEntitySelectionRow[] = [];
    for (const workspaceRow of rows) {
        const workspaceNameMatches =
            !hasSearch || workspaceRow.name.toLowerCase().includes(trimmed);
        const children = (workspaceRow.children ?? []).filter((child) => {
            const childNameMatches =
                !hasSearch || child.name.toLowerCase().includes(trimmed);
            const nameOk = workspaceNameMatches || childNameMatches;
            const industryOk =
                !hasIndustryFilter ||
                child.industries.some((industry) =>
                    selectedIndustries.includes(industry),
                );
            const basisOk =
                !hasBasisFilter ||
                child.bases.some((basis) => selectedBases.includes(basis));
            return nameOk && industryOk && basisOk;
        });
        if (children.length > 0) {
            result.push({ ...workspaceRow, children });
        }
    }
    return result;
}
